import React, { useEffect, useRef, useState } from 'react';
import {
    Container, Typography, Card, CardContent, Grid,
    Box, Divider, Button, Dialog, DialogTitle,
    DialogContent, TextField, DialogActions, Pagination, LinearProgress
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { Pie, Bar, Line } from 'react-chartjs-2';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import TimelineIcon from '@mui/icons-material/Timeline';
import axiosInstance from '../axiosConfig';
import Layout from './Layout';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
    Chart as ChartJS,
    ArcElement, BarElement, LineElement, PointElement,
    CategoryScale, LinearScale, Tooltip, Legend, Title
} from 'chart.js';

import { AssignmentTurnedIn, Pending, DoneAll } from '@mui/icons-material';

ChartJS.register(
    ArcElement, BarElement, LineElement, PointElement,
    CategoryScale, LinearScale, Tooltip, Legend, Title
);

const ReportPage = () => {
    const { surveyId } = useParams();
    const [survey, setSurvey] = useState(null);
    const [surveyData, setSurveyData] = useState({
        totalResponses: 0,
        completedResponses: 0,
        responses: []
    });
    const [pieChartImage, setPieChartImage] = useState('');
    const [barChartImage, setBarChartImage] = useState('');
    const [lineChartImage, setLineChartImage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loginOpen, setLoginOpen] = useState(true);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const responsesPerPage = 5;
    const [currentPage, setCurrentPage] = useState(1);
    const pieChartRef = useRef(null);
    const barChartRef = useRef(null);
    const lineChartRef = useRef(null);

    useEffect(() => {
        if (!loginOpen) fetchSurveyDetails();
    }, [loginOpen]);

    const fetchSurveyDetails = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/surveys/${surveyId}`);
            setSurvey(response.data);

            const completedResponse = await axiosInstance.get(`/api/surveys/${surveyId}/completed`);
            setSurveyData(completedResponse.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const captureChartImages = () => {
        setPieChartImage(pieChartRef.current.toBase64Image());
        setBarChartImage(barChartRef.current.toBase64Image());
        setLineChartImage(lineChartRef.current.toBase64Image());
    };

    const handleLogin = async () => {
        try {
            const response = await axiosInstance.post('/api/reports/validate', {
                surveyId,
                username,
                password,
            });

            if (response.status === 200) setLoginOpen(false);
        } catch (error) {
            setLoginError('Invalid credentials or network issue.');
        }
    };

    const downloadPDF = () => {
        const doc = new jsPDF('p', 'mm', 'a4');

        doc.text('Survey Report', 10, 10);
        doc.text(`Survey Name: ${survey?.surveyName}`, 10, 20);

        doc.addImage(pieChartImage, 'PNG', 10, 30, 90, 60);
        doc.addImage(barChartImage, 'PNG', 110, 30, 90, 60);
        doc.addImage(lineChartImage, 'PNG', 10, 100, 190, 60);

        doc.text('Responses:', 10, 170);
        surveyData.responses.forEach((response, index) => {
            doc.text(
                `${index + 1}. Participant: ${response.participantId}, Completed: ${response.completed ? 'Yes' : 'No'}`,
                10,
                180 + index * 10
            );
        });

        doc.save('survey-report.pdf');
    };
    const chartOptions = {
        maintainAspectRatio: false,
        animation: {
            onComplete: captureChartImages
        }
    };

    const handlePageChange = (event, value) => setCurrentPage(value);

    const pieChartData = {
        labels: ['Completed', 'In Progress'],
        datasets: [
            {
                data: [surveyData.completedResponses, surveyData.totalResponses - surveyData.completedResponses],
                backgroundColor: ['#4caf50', '#ff9800'],
            },
        ],
    };

    const barChartData = {
        labels: ['Section 1', 'Section 2', 'Section 3'],
        datasets: [
            {
                label: 'Responses per Section',
                data: [12, 15, 9],
                backgroundColor: '#42a5f5',
            },
        ],
    };

    const lineChartData = {
        labels: ['Day 1', 'Day 2', 'Day 3'],
        datasets: [
            {
                label: 'Response Trend',
                data: [5, 10, 15],
                borderColor: '#66bb6a',
                fill: false,
            },
        ],
    };

    const completionPercentage = Math.round(
        (surveyData.completedResponses / surveyData.totalSurvey) * 100
    );

    return (
        <Layout>
            {/* Login Dialog */}
            <Dialog open={loginOpen} disableEscapeKeyDown>
                <DialogTitle>Login</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Username"
                        fullWidth
                        margin="normal"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    {loginError && (
                        <Typography color="error" variant="body2">
                            {loginError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                <Button onClick={handleLogin} variant="contained" color="primary">
                        Login
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Report Content */}
            <Box sx={{ filter: loginOpen ? 'blur(5px)' : 'none', transition: 'all 0.3s ease-in-out' }}>
                <Container maxWidth="lg" sx={{ mt: 5 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Survey Report: {survey?.surveyName}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />


                    {/* Survey Description */}
                    <Box
                        sx={{
                            mb: 4,
                            p: 4,
                            borderRadius: '12px',
                            boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #d0d0d0',
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                mb: 3,
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#1E2742',
                            }}
                        >
                            <AssessmentIcon sx={{ mr: 1, color: '#4caf50', fontSize: '30px' }} />
                            Survey Overview
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Description */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 0,
                                        fontSize: '16px',
                                        color: '#555',
                                    }}
                                >
                                    <strong
                                        style={{
                                            minWidth: '140px',
                                            display: 'inline-block',
                                            color: '#333',
                                        }}
                                    >
                                        Description:
                                    </strong>
                                    {survey?.surveyDescription || 'No description available.'}
                                </Typography>
                            </Grid>

                            {/* University */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 0,
                                        fontSize: '16px',
                                        color: '#555',
                                    }}
                                >
                                    <strong
                                        style={{
                                            minWidth: '140px',
                                            display: 'inline-block',
                                            color: '#333',
                                        }}
                                    >
                                        University:
                                    </strong>
                                    {survey?.universityName || 'N/A'}
                                </Typography>
                            </Grid>

                            {/* Valid Till */}
                            <Grid item xs={12} md={3}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 2,
                                        fontSize: '16px',
                                        color: '#555',
                                    }}
                                >
                                    <strong
                                        style={{
                                            minWidth: '120px',
                                            display: 'inline-block',
                                            color: '#333',
                                        }}
                                    >
                                        Valid Till:
                                    </strong>
                                    {new Date(survey?.validTill).toLocaleDateString() || 'N/A'}
                                </Typography>
                            </Grid>

                            {/* Total Links */}
                            <Grid item xs={12} md={3}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '16px',
                                        color: '#555',
                                    }}
                                >
                                    <strong
                                        style={{
                                            minWidth: '120px',
                                            display: 'inline-block',
                                            color: '#333',
                                        }}
                                    >
                                        Total Links:
                                    </strong>
                                    {survey?.uniqueLinksCount || 0}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>


                    {/* KPI Section */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}>
                            <Card
                                sx={{
                                    p: 2,
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    transition: '0.3s',
                                    '&:hover': { transform: 'scale(1.05)' }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <AssignmentTurnedIn sx={{ fontSize: 50, color: '#1976d2' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                                        {surveyData.totalResponses}
                                    </Typography>
                                    <Typography variant="body1">Total Responses</Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Card
                                sx={{
                                    p: 2,
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    transition: '0.3s',
                                    '&:hover': { transform: 'scale(1.05)' }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <DoneAll sx={{ fontSize: 50, color: '#4caf50' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                                        {surveyData.completedResponses}
                                    </Typography>
                                    <Typography variant="body1">Completed Responses</Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Card
                                sx={{
                                    p: 2,
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    transition: '0.3s',
                                    '&:hover': { transform: 'scale(1.05)' }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Pending sx={{ fontSize: 50, color: '#ff9800' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                                        {surveyData.totalResponses - surveyData.completedResponses}
                                    </Typography>
                                    <Typography variant="body1">In Progress</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Progress Bar Section */}
                    <Box sx={{ mt: 4, mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Survey Completion Progress
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={completionPercentage || 0}
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                            {completionPercentage || 0}% Completed
                        </Typography>
                    </Box>


                    <Divider sx={{ mb: 3 }} />

                    {/* Charts Section */}
                    <Grid container spacing={4}>
                        {/* Row 1: Response Breakdown - Pie Chart */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    p: 5,
                                    height: '350px',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    overflow: 'hidden', // Prevent overflow
                                }}
                            >
                                <Typography variant="h6">
                                    <AssessmentIcon sx={{ mr: 1 }} /> Response Breakdown
                                </Typography>
                                <Pie ref={pieChartRef} data={pieChartData} options={chartOptions} />
                            </Card>
                        </Grid>

                        {/* Row 2: Responses per Section - Bar Chart */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    p: 5,
                                    height: '350px',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    overflow: 'hidden', // Prevent overflow
                                }}
                            >
                                <Typography variant="h6">
                                    <InsightsIcon sx={{ mr: 1 }} /> Responses per Section
                                </Typography>
                                <Bar ref={barChartRef} data={barChartData} options={chartOptions} />
                            </Card>
                        </Grid>

                        {/* Row 3: Response Trends - Line Chart */}
                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    p: 5,
                                    height: '350px',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    overflow: 'hidden', // Prevent overflow
                                }}
                            >
                                <Typography variant="h6">
                                    <TimelineIcon sx={{ mr: 1 }} /> Response Trends
                                </Typography>
                                <Line ref={lineChartRef} data={lineChartData} options={chartOptions} />
                            </Card>
                        </Grid>
                    </Grid>


                    {/* Pagination */}
                    <Pagination
                        count={Math.ceil(surveyData.responses.length / responsesPerPage)}
                        page={currentPage}
                        onChange={handlePageChange}
                        sx={{ mt: 3 }}
                    />
                    <Button onClick={downloadPDF} variant="contained" color="primary" sx={{ mt: 3 }}>
                        Download PDF
                    </Button>
                </Container>
            </Box>
        </Layout>
    );
};

export default ReportPage;
